import PltDatapoint from './datapoints/PltDatapoint';
import TraceDatapoint from './datapoints/TraceDatapoint';
import captureServiceActionOptics from './captureServiceActionOptics';
import optionallyTraceDatapoint from './utils/optionallyTraceDatapoint';
import { addCommonProperties, logSessionDatapoint } from './utils/logSessionDatapoint';
import { AnalyticsOptions, FlightControl, DatapointStatus } from './types/DatapointEnums';
import {
    AriaDatapoint,
    VerbosePerfEventType,
    isEventQueryStringEnabled,
} from './datapoints/AriaDatapoint';
import { ariaFlush, createEventProperties, getLogger, initializeLogManager } from './AriaWrapper';
import { AWTPiiKind } from '@aria/webjs-sdk';
import { DatapointOptions, DatapointVariant, getCachedInfo } from 'owa-analytics-actions';
import { getUserConfiguration } from 'owa-session-store';
import { isFeatureEnabled, getFeatureFlag } from 'owa-feature-flags';
import { safeStringify, validatePropertyBag } from './utils/validatePropertyBag';
import ErrorDatapoint, { ErrorDatapointName } from './datapoints/ErrorDatapoint';
import { isCurrentSessionInSample } from './utils/getSessionSampleBucket';
import { getClientVersion, getBootType, getLogicalRing } from 'owa-config';
import { getCLPAriaTenantTokens } from './utils/getMailAriaTenantTokens';
import { TraceErrorObject, registerTracerListener, TraceLevel, onGlobalError } from 'owa-trace';
import getResourceTimingForUrl from './utils/getResourceTimingForUrl';
import logSigsDatapoint from './logSigsDatapoint';
import UsageDatapoint from './datapoints/UsageDatapoint';
import calculateTiming from './utils/calculateTiming';
import captureAssetsOptics from './captureAssetsOptics';
import { setErrorHandler } from 'owa-exception-handler';
import { registerCreateServiceResponseCallback as registerOwsCreateServiceResponseCallback } from 'owa-service/lib/fetchWithRetry';
import { registerCreateServiceResponseCallback as registerOwsPrimeCreateServiceResponseCallback } from 'owa-ows-gateway/lib/registerCreateServiceResponseCallback';
import { getNonBootUserConfiguration } from 'owa-nonboot-userconfiguration-manager';
import { getPltData } from 'owa-config/lib/bootstrapOptions';
import { PerformanceDatapoint } from './datapoints/PerformanceDatapoint';
import { isAnonymousUser } from 'owa-config/lib/anonymousUtils';
import isDevEnvironment from './utils/isDevEnvironment';
import { isTracingEnabled } from './utils/isTracingEnabled';

function getTenantLoggers(analyticsOptions: AnalyticsOptions, options: DatapointOptions): string[] {
    return options?.tenantTokens ? options.tenantTokens : analyticsOptions.ariaTenantTokens;
}

let resolveInitalizeAnalytics: (options: AnalyticsOptions) => void;
//tslint:disable:promise-must-complete
let initializeAnalyticsPromise: Promise<AnalyticsOptions> = new Promise(resolve => {
    resolveInitalizeAnalytics = resolve;
});

export function getInitializeAnalyticsPromse() {
    return initializeAnalyticsPromise;
}

export let defaultAnalyticsOptions: AnalyticsOptions = {
    ariaTenantTokens: [],
    isTesting: false,
    maxErrorsPerSession: 100,
    flightControls: null,
    verboseWhiteListEvents: [],
};

//tslint:enable:promise-must-complete
export async function initializeAnalytics(analyticsOptions: AnalyticsOptions) {
    // if no aria tokens are passed in, there is no point
    if (analyticsOptions?.ariaTenantTokens && analyticsOptions.ariaTenantTokens.length > 0) {
        analyticsOptions = { ...defaultAnalyticsOptions, ...analyticsOptions };

        if (isFeatureEnabled('ring-dogfood') || isFeatureEnabled('an-no-sample-ppe')) {
            delete analyticsOptions.flightControls;
        }

        let context = initializeLogManager(analyticsOptions.ariaTenantTokens[0]);
        var userConfiguration = getUserConfiguration();
        if (context) {
            context.setAppVersion(getClientVersion());
            if (userConfiguration) {
                context.setUserTimeZone(userConfiguration.UserOptions.TimeZone);
                context.setUserLanguage(userConfiguration.SessionSettings.UserCulture);
            }
        }

        resolveInitalizeAnalytics(analyticsOptions);

        let nonBootSettings;
        let bootType;
        try {
            nonBootSettings = isAnonymousUser() ? null : await getNonBootUserConfiguration();
            bootType = await getBootType();
        } finally {
            analyticsOptions.ariaTenantTokens.forEach(token => {
                logSessionDatapoint(
                    analyticsOptions,
                    token,
                    userConfiguration,
                    nonBootSettings,
                    bootType
                );
            });
            logDatapoint(new PltDatapoint(bootType));
        }

        // the errors are backfilled since the begininning of the app. So we can set the error handler here
        // since this is the first time that we can log the errors
        setErrorHandler(onGlobalError);
        if (window.performance?.timing) {
            captureAssetsOptics([calculateTiming(window.performance.timing, 'indexPage')]);
        }
        const info = getCachedInfo();
        info.errors.forEach(logErrorDatapoint);
        info.network.forEach(networkArgs => captureServiceActionOptics.apply(null, networkArgs));
        info.traces.forEach(traceArgs => logTraceDatapoint(traceArgs.message, traceArgs.level));
        registerTracerListener((message, traceLevel, errorDetails) => {
            if (traceLevel == TraceLevel.Error) {
                logErrorDatapoint(errorDetails);
            }
            logTraceDatapoint(message, traceLevel);
        });
        registerOwsCreateServiceResponseCallback(captureServiceActionOptics);
        registerOwsPrimeCreateServiceResponseCallback(captureServiceActionOptics);
        const pltData = getPltData();
        if (pltData) {
            const dp = new PerformanceDatapoint('PageLoadTime', {
                isCore: true,
                isCosmosDatapoint: true,
            });
            dp.end(
                pltData.time,
                getDatapointStatusFromBootResult(pltData.status, pltData.isServer)
            );
        }
    }
}

function getDatapointStatusFromBootResult(status: string, isServer: boolean) {
    if (status == 'ok') {
        return DatapointStatus.Success;
    } else if (status == 'fail') {
        return isServer ? DatapointStatus.ServerError : DatapointStatus.ClientError;
    }
    return DatapointStatus.UserError;
}

export function getOTelAddinsLogger(tenantToken: string) {
    return getLogger(tenantToken);
}

export function flush(): void {
    ariaFlush();
}

function getFlightControl(
    datapoint: AriaDatapoint,
    analyticsOptions: AnalyticsOptions
): FlightControl {
    // Application that do not initialize analytics, such as Storybook, have an undefined appAnalyticsOptions
    if (analyticsOptions?.flightControls && (!datapoint.options || !datapoint.options.isCore)) {
        return analyticsOptions.flightControls[datapoint.getEventType()];
    }
    return null;
}

function getIsSessionSampled(datapoint: AriaDatapoint) {
    return (
        !datapoint.options ||
        !datapoint.options.sessionSampleRate ||
        isCurrentSessionInSample(datapoint.options.sessionSampleRate)
    );
}

function getDoesVariantApply(datapoint: AriaDatapoint) {
    switch (datapoint.options?.variant) {
        case DatapointVariant.None:
            return false;
        case DatapointVariant.WarmOnly:
            return datapoint.sessionOccurence == 1;
        case DatapointVariant.ColdOnly:
            return datapoint.sessionOccurence > 1;
        default:
            return true;
    }
}

let errorsInSession = 0;
export function shouldLogDatapoint(
    datapoint: AriaDatapoint,
    analyticsOptions: AnalyticsOptions
): boolean {
    let eventType = datapoint.getEventType();
    // if we enable with a query string parameter, we should always allow this datapoint to get logged
    if (isEventQueryStringEnabled(eventType)) {
        return true;
    }

    // don't log client_verbose datapoints if there is no whitelist or the datapoint is not on the whitelist
    if (
        eventType == VerbosePerfEventType &&
        (!analyticsOptions.verboseWhiteListEvents ||
            analyticsOptions.verboseWhiteListEvents.indexOf(datapoint.eventName) == -1)
    ) {
        return false;
    }

    if (eventType == ErrorDatapointName) {
        errorsInSession++;
        if (errorsInSession > analyticsOptions.maxErrorsPerSession) {
            return false;
        }
    }

    let control = getFlightControl(datapoint, analyticsOptions);
    if (control && !getFeatureFlag(control.flight)) {
        return false;
    }

    // if logEvery is set to 5, then we should log every 5th of these datapoints
    if (datapoint.options?.logEvery) {
        if ((datapoint.sessionOccurence - 1) % datapoint.options.logEvery != 0) {
            return false;
        }
    }

    if (datapoint.options?.ring && datapoint.options.ring != getLogicalRing()) {
        return false;
    }

    return getIsSessionSampled(datapoint) && getDoesVariantApply(datapoint);
}

export function logDatapoint(datapoint: AriaDatapoint): void {
    initializeAnalyticsPromise.then(analyticsOptions => {
        let isFullVerboseLoggingEnabled = isFeatureEnabled('an-full-logging');
        let flightControl = getFlightControl(datapoint, analyticsOptions);
        if (!shouldLogDatapoint(datapoint, analyticsOptions) && !isFullVerboseLoggingEnabled) {
            return;
        }

        optionallyTraceDatapoint(datapoint);

        var loggerTokens = getTenantLoggers(analyticsOptions, datapoint.options);
        let eventType = datapoint.getEventType();
        for (var i = 0; i < loggerTokens.length; i++) {
            let e = createEventProperties(
                isFullVerboseLoggingEnabled ? VerbosePerfEventType : eventType
            );
            if (isFullVerboseLoggingEnabled) {
                e.properties['OriginalEvent'] = eventType;
            }

            var keys = Object.keys(datapoint.properties);
            for (var ii = 0; ii < keys.length; ii++) {
                let property = datapoint.properties[keys[ii]];
                e.properties[keys[ii]] = property.value;
            }

            addCommonProperties(e);
            e.properties['Sampled'] = flightControl?.rate ? flightControl.rate : 100;

            if (datapoint.propertyBag) {
                e.properties['MiscData'] = validatePropertyBag(datapoint);
            }
            if (datapoint.piiBag) {
                e.properties['PiiData'] = {
                    value: safeStringify(datapoint.piiBag),
                    pii: AWTPiiKind.GenericData,
                };
            }

            // let's not actually log datapoints if we are gulping or in a branch or a tds box
            if (!isDevEnvironment(analyticsOptions)) {
                // we need to create a copy of the datapoint for each event, because there is a bug
                // in the aria code which will force the events to be sent to the same tenant if
                // the event is the same instance
                getLogger(loggerTokens[i]).logEvent(e);
            }
        }
    });
}

const memoryErrors = ['out of memory', 'not enough storage is available'];
function isMemoryError(message: string): boolean {
    if (message) {
        for (var ii = 0; ii < memoryErrors.length; ii++) {
            if (message.toLowerCase().indexOf(memoryErrors[ii]) > -1) {
                return true;
            }
        }
    }
    return false;
}

function logTraceDatapoint(message: string, level: TraceLevel) {
    if (isTracingEnabled(level)) {
        logDatapoint(new TraceDatapoint(message, level));
    }
}

function logErrorDatapoint(errorDetails: TraceErrorObject) {
    try {
        const datapointName =
            (errorDetails.networkError && 'NETWORK_ERROR') ||
            (isMemoryError(errorDetails.message) && 'MEMORY_ERROR');

        const dp = datapointName
            ? new UsageDatapoint(datapointName, {
                  message: errorDetails.message,
                  diagnosticInfo: errorDetails.diagnosticInfo,
              })
            : new ErrorDatapoint(errorDetails);

        logDatapoint(dp);
    } catch {
        // TODO OW 48077
    }
}

export function logCLPAriaDataPoint(clpAriaData: Object) {
    let clpAriaEventProperties = createEventProperties('CLPLabel' /* EventName */);
    Object.keys(clpAriaData).map(key => {
        clpAriaEventProperties.properties[key] = clpAriaData[key];
    });
    getLogger(getCLPAriaTenantTokens()).logEvent(clpAriaEventProperties);
}

export { getResourceTimingForUrl, logSigsDatapoint };
